import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { DownloadIcon } from '@heroicons/react/outline';
import { useAsyncFn } from 'react-use';
import { AccountEntity } from '@shared/api/accountConfig/accountConfig.interface';
import { giftBrandSellerDeliveryPopulate } from '@shared/api/giftDelivery/giftDelivery.controller';
import { GiftDeliveryForAdmin } from '@shared/api/giftDelivery/giftDelivery.interface';
import { SettlementInfo } from '@shared/api/giftItemBrand/giftItemBrand.interface';
import {
  deleteGiftSupplySettlement,
  listGiftSupplySettlements,
  patchGiftSupplySettlement,
  settleGiftSupply,
} from '@shared/api/giftSupplySettlement/giftSupplySettlement.controller';
import {
  GiftSettlementStatus,
  GiftSupplySettlement,
  SettlementPendingEntry,
} from '@shared/api/giftSupplySettlement/giftSupplySettlement.interface';
import { getSettlementPending } from '@shared/api/settlementPending/settlementPending.controller';
import { Loading } from '@shared/components/Loading';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';

import { SupplySettlementDetailModal } from '../../giftBrand/components/SupplySettlementDetailModal';
import { DateFilter } from '../components/giftSettlement/DateFilter';
import { PaidConfirmModal } from '../components/giftSettlement/PaidConfirmModal';
import { SettlementCreateModal } from '../components/giftSettlement/SettlementCreateModal';
import { SettlementEditModal } from '../components/giftSettlement/SettlementEditModal';
import { formatDate, getItemName } from '../utils/delivery.utils';
import { exportSupplyHistorySummaryExcel } from '../utils/giftSettlementHistoryExcel';
import { statusColor } from '../utils/rebate-settlement.utils';
import { Toast } from '../components/Toast';
import { useRowSelection } from '../hooks/useRowSelection';
import { useToast } from '../hooks/useToast';
import {
  computeSettlementLines,
  formatWon,
  getSettlementBrandId,
  settlementDisplayTotal,
  SettlementLine,
} from '../utils/settlement.utils';

// ─────────────────────────────────────────────
// 배송업체 정산 관리 (구 GiftSettlementPage의 업체정산 탭 분리)
// 공급 업체(giftItemBrand)에 지급하는 배송형(supply) 정산 전용 페이지.
// 상조 정산(배송형·쿠폰형)은 GiftSettlementAgencyPage로 분리됐다.
// ─────────────────────────────────────────────

// 라벨은 화면 계열마다 다르다(이 페이지 '정산집계' vs rebate-settlement.utils '정산예정')
// — 색상만 공용 statusColor를 쓰고 라벨은 로컬 유지 (통일은 카피 결정 필요)
const statusLabel = (s: GiftSettlementStatus) =>
  s === GiftSettlementStatus.Paid ? '지급완료' : '정산집계';

const getSupplyAccountEntity = (d: GiftDeliveryForAdmin): string | undefined => {
  const brand = d.giftItemBrand;
  if (!brand || typeof brand !== 'object') return undefined;
  return (brand as any).settlementInfo?.accountEntity as string | undefined;
};

const accountEntityBadge = (entity: string | undefined) => {
  if (!entity || entity === 'NotDecided') return null;
  const isMari = entity === 'Mari';
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
        isMari ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'
      }`}
    >
      {isMari ? '마리' : '가온'} 경유
    </span>
  );
};

// ───────────────────────── 업체정산(supply) 예정 ─────────────────────────

interface PendingSectionProps {
  isAdmin: boolean;
  onSettled: () => void;
  /** GiftAgent(마리) 조회 뷰: 마리 정산건만 노출하고 경유 칩을 숨긴다 */
  giftAgentView?: boolean;
}

const SupplyPendingSection: FC<PendingSectionProps> = ({
  isAdmin,
  onSettled,
  giftAgentView = false,
}) => {
  const toast = useToast();
  const [{ loading, value: entries }, fetchPending] = useAsyncFn(
    () => getSettlementPending('supply', {}, LowerRole.Admin),
    [],
  );

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  // 정산정보(settlementInfo)는 settlement-pending 응답의 delivery.giftItemBrand에
  // 이미 fully populate 되어 있다. 별도 /admin/gift-item-brand 호출(GiftAgent는 admin
  // 롤이 없어 401)을 하지 않고, 각 배송이 들고 온 공급사 정보에서 직접 모은다.
  const settlementByBrand = useMemo(() => {
    const map = new Map<string, SettlementInfo | undefined>();
    (entries ?? []).forEach((e) => {
      const brand = e.delivery.giftItemBrand;
      if (brand && typeof brand === 'object') {
        map.set(brand._id, (brand as { settlementInfo?: SettlementInfo }).settlementInfo);
      }
    });
    return (brandId?: string) => (brandId ? map.get(brandId) : undefined);
  }, [entries]);

  // 배송 _id → 정산 라인. 택배비 묶음(같은 송장 1회) 계산은 집합 단위라 전체를 한 번에 계산한 뒤
  // id로 색인해 행 렌더링에서 O(1)로 조회한다.
  const lineByDelivery = useMemo(() => {
    const lines = computeSettlementLines(
      (entries ?? []).map((e) => e.delivery),
      settlementByBrand,
      new Map((entries ?? []).map((e) => [e.delivery._id, e.preview])),
    );
    const map = new Map<string, SettlementLine>();
    lines.forEach((l) => map.set(l.delivery._id, l));
    return map;
  }, [entries, settlementByBrand]);

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const { selected, toggleRow, toggleGroup, clear: clearSelected } = useRowSelection();
  const [modal, setModal] = useState<{
    open: boolean;
    brandId: string;
    brandName: string;
    ids: string[];
    amount: number;
  }>({ open: false, brandId: '', brandName: '', ids: [], amount: 0 });

  const filtered = useMemo(() => {
    const fromT = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : null;
    const toT = dateTo ? new Date(`${dateTo}T23:59:59`).getTime() : null;
    return (entries ?? []).filter((e) => {
      // GiftAgent(마리)는 업체정산 주체가 마리인 건만 본다 (가온 계약 도매가 비노출)
      if (giftAgentView && getSupplyAccountEntity(e.delivery) !== AccountEntity.Mari) {
        return false;
      }
      if (fromT !== null || toT !== null) {
        const at = e.delivery.shipping?.deliveredAt;
        if (!at) return false;
        const t = new Date(at).getTime();
        if (fromT !== null && t < fromT) return false;
        if (toT !== null && t > toT) return false;
      }
      return true;
    });
  }, [entries, dateFrom, dateTo, giftAgentView]);

  const groups = useMemo(() => {
    const map = new Map<
      string,
      { brandName: string; accountEntity?: string; entries: SettlementPendingEntry[] }
    >();
    filtered.forEach((e) => {
      const id = getSettlementBrandId(e.delivery) ?? '__none__';
      const brand = e.delivery.giftItemBrand;
      const name =
        typeof brand === 'object' && brand
          ? brand.name
          : id === '__none__'
          ? '미지정'
          : id;
      const accountEntity = getSupplyAccountEntity(e.delivery);
      if (!map.has(id)) map.set(id, { brandName: name, accountEntity, entries: [] });
      map.get(id)!.entries.push(e);
    });
    return Array.from(map.entries()).map(([id, g]) => ({ id, ...g }));
  }, [filtered]);

  const handleSettle = async ({
    year,
    month,
    memo,
    overrideAmount,
  }: {
    year: number;
    month: number;
    memo: string;
    overrideAmount: number;
  }) => {
    try {
      // 같은 (업체, 정산월) 정산이 이미 있으면: Draft는 합치고, Paid는 차단·안내
      const existing = await listGiftSupplySettlements(
        { giftItemBrand: modal.brandId, year, month },
        [],
        {},
        LowerRole.Admin,
      );
      const dup = existing[0];
      if (dup) {
        if (dup.status === GiftSettlementStatus.Paid) {
          toast.show(
            `이미 지급완료된 ${year}년 ${month}월 정산이 있습니다. 수정하려면 먼저 Draft로 되돌리세요.`,
          );
          return;
        }
        // 기존 월 정산에 합칠 땐 전체 라인이 재계산되므로 override는 적용하지 않는다.
        await patchGiftSupplySettlement(dup._id, { addDeliveryIds: modal.ids });
        toast.show(`${year}년 ${month}월 정산에 ${modal.ids.length}건 추가 완료`);
      } else {
        const result = await settleGiftSupply({
          year,
          month,
          target: { giftItemBrand: modal.brandId },
          giftDeliveryIds: modal.ids,
          memo: memo || undefined,
        });
        const skipped = result.skipped.length;
        if (skipped > 0) {
          const reasons = Array.from(new Set(result.skipped.map((s) => s.reason))).join(
            ', ',
          );
          toast.show(
            `${result.includedCount}건 집행 완료. ${skipped}건 제외 (${reasons})`,
          );
        } else {
          toast.show(`${result.includedCount}건 집행 완료`);
        }
        // 실제 정산액을 직접 입력했고 계산값과 다르면 그 금액으로 동결(신규 정산만)
        // 업체정산 라인은 원래 정수(도매가×수량+실비) — 반올림 없이 그대로 비교
        if (overrideAmount !== modal.amount) {
          await patchGiftSupplySettlement(result.settlement._id, {
            overrideAmount: true,
            totalAmount: overrideAmount,
          });
        }
      }
      setModal((m) => ({ ...m, open: false }));
      clearSelected();
      fetchPending();
      onSettled();
    } catch (e: any) {
      const msg = e?.response?.data?.message || '집행 실패';
      toast.show(`오류: ${msg}`);
      throw e;
    }
  };

  if (loading)
    return (
      <div className="center-box py-16">
        <Loading />
      </div>
    );

  return (
    <div>
      <DateFilter
        dateFrom={dateFrom}
        dateTo={dateTo}
        onFrom={setDateFrom}
        onTo={setDateTo}
      />

      {groups.length === 0 ? (
        <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow">
          미정산 내역이 없습니다.
        </div>
      ) : (
        groups.map((group) => {
          const groupIds = group.entries.map((e) => e.delivery._id);
          const selectedInGroup = groupIds.filter((id) => selected.has(id));
          const allGroupSel =
            groupIds.length > 0 && groupIds.every((id) => selected.has(id));
          const groupTotal = group.entries.reduce(
            (s, e) =>
              s + (lineByDelivery.get(e.delivery._id)?.total ?? e.preview.lineAmount),
            0,
          );
          const selectedTotal = group.entries
            .filter((e) => selected.has(e.delivery._id))
            .reduce(
              (s, e) =>
                s + (lineByDelivery.get(e.delivery._id)?.total ?? e.preview.lineAmount),
              0,
            );

          return (
            <div
              key={group.id}
              className="mb-6 overflow-x-auto rounded-lg bg-white shadow"
            >
              <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-4 py-2.5">
                <div className="flex items-center gap-3">
                  {isAdmin && (
                    <input
                      type="checkbox"
                      checked={allGroupSel}
                      onChange={() => toggleGroup(group.entries)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                  )}
                  <span className="font-semibold text-gray-800">{group.brandName}</span>
                  {!giftAgentView && accountEntityBadge(group.accountEntity)}
                  <span className="text-xs text-gray-400">
                    {group.entries.length}건 · {formatWon(groupTotal)}
                  </span>
                </div>
                {isAdmin && (
                  <button
                    disabled={selectedInGroup.length === 0}
                    onClick={() =>
                      setModal({
                        open: true,
                        brandId: group.id,
                        brandName: group.brandName,
                        ids: selectedInGroup,
                        amount: selectedTotal,
                      })
                    }
                    className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    선택 집행 ({selectedInGroup.length}건
                    {selectedInGroup.length > 0 ? ` · ${formatWon(selectedTotal)}` : ''})
                  </button>
                )}
              </div>
              <table className="min-w-full table-fixed divide-y divide-gray-200 text-sm">
                <colgroup>
                  {isAdmin && <col className="w-10" />}
                  <col className="w-[18%]" />
                  <col className="w-[11%]" />
                  <col className="w-[11%]" />
                  <col className="w-[9%]" />
                  <col className="w-[14%]" />
                  <col className="w-[12%]" />
                  <col className="w-[8%]" />
                  <col className="w-[8%]" />
                  <col className="w-[9%]" />
                </colgroup>
                <thead className="bg-gray-50 text-xs text-gray-500">
                  <tr>
                    {isAdmin && <th className="px-3 py-2" />}
                    <th className="px-3 py-2 text-left">상품</th>
                    <th className="px-3 py-2 text-left">배송요청일</th>
                    <th className="px-3 py-2 text-left">배송완료일</th>
                    <th className="px-3 py-2 text-left">택배사</th>
                    <th className="px-3 py-2 text-left">송장번호</th>
                    <th className="px-3 py-2 text-right">도매단가(+소량)</th>
                    <th className="px-3 py-2 text-right">× 수량</th>
                    <th className="px-3 py-2 text-right">+ 배송비</th>
                    <th className="px-3 py-2 text-right">= 정산금액</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {group.entries.map(({ delivery: d, preview: p }) => {
                    const l = lineByDelivery.get(d._id);
                    return (
                      <tr
                        key={d._id}
                        className={`hover:bg-gray-50 ${
                          selected.has(d._id) ? 'bg-indigo-50' : ''
                        }`}
                        onClick={() => isAdmin && toggleRow(d._id)}
                        style={{ cursor: isAdmin ? 'pointer' : undefined }}
                      >
                        {isAdmin && (
                          <td className="px-3 py-2.5">
                            <input
                              type="checkbox"
                              checked={selected.has(d._id)}
                              readOnly
                              className="h-4 w-4 rounded border-gray-300"
                            />
                          </td>
                        )}
                        <td className="px-3 py-3">
                          <p className="break-words font-medium">{getItemName(d)}</p>
                          <p className="text-[11px] text-gray-400">
                            {d.recipient?.name || '-'}
                          </p>
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                          {formatDate(d.recipient?.submittedAt)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-xs font-semibold text-gray-800">
                          {formatDate(d.shipping?.deliveredAt)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-600">
                          {d.shipping?.carrier || '-'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 font-mono text-xs text-gray-600">
                          {d.shipping?.trackingNumber || '-'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums">
                          {(l?.wholePrice ?? p.unitAmount).toLocaleString()}
                          {l && l.extraUnitPrice > 0 && (
                            <span className="text-amber-600">
                              (+{l.extraUnitPrice.toLocaleString()})
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums">
                          {l?.quantity ?? p.quantity}개
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums">
                          {l?.deliveryFeeMode === 'orderDeliveryFee' ? (
                            <span>
                              {l.orderDeliveryFee.toLocaleString()}
                              <span className="block text-[10px] text-gray-500">
                                {l.orderDeliveryFee > 0
                                  ? '고객 결제액 · 주문당 1회'
                                  : '배송비 없음 또는 다른 배송건에 배정'}
                              </span>
                            </span>
                          ) : l?.deliveryFeeMode === 'included' ? (
                            <span className="text-xs text-gray-500">단가에 포함</span>
                          ) : l ? (
                            l.courierFee > 0 ? (
                              l.courierFee.toLocaleString()
                            ) : l.courierFreeByQuantity ? (
                              <span className="text-[11px] text-gray-400">
                                무료({l.courierFreeMinQuantity}충족)
                              </span>
                            ) : l.courierBundled ? (
                              <span className="text-[11px] text-gray-400">묶음면제</span>
                            ) : l.courierFeeBase > 0 ? (
                              '0'
                            ) : (
                              <span className="text-gray-300">-</span>
                            )
                          ) : p.surcharge > 0 ? (
                            <span className="text-amber-600">
                              +{p.surcharge.toLocaleString()}
                            </span>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-right">
                          <span className="font-semibold tabular-nums text-gray-800">
                            {formatWon(l?.total ?? p.lineAmount)}
                          </span>
                          {!(l?.hasSettlementInfo ?? p.hasSettlementInfo) && (
                            <span className="block text-[10px] text-amber-600">
                              정산정보 미설정
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-50 text-sm font-semibold">
                  <tr>
                    <td className="px-3 py-2.5 text-gray-500" colSpan={isAdmin ? 7 : 6}>
                      합계 ({group.entries.length}건)
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-gray-500">
                      {group.entries.reduce(
                        (s, e) =>
                          s +
                          (lineByDelivery.get(e.delivery._id)?.quantity ??
                            e.preview.quantity),
                        0,
                      )}
                      개
                    </td>
                    <td className="px-3 py-2.5" />
                    <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-rose-700">
                      {formatWon(groupTotal)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          );
        })
      )}

      <SettlementCreateModal
        open={modal.open}
        targetName={modal.brandName}
        count={modal.ids.length}
        amount={modal.amount}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
        onConfirm={handleSettle}
      />
      <Toast message={toast.message} durationMs={toast.durationMs} />
    </div>
  );
};

// ───────────────────────── 업체정산(supply) 내역 ─────────────────────────

interface HistorySectionProps {
  isAdmin: boolean;
  refreshTrigger: number;
  /** GiftAgent(마리) 조회 뷰: 마리 정산건만 노출하고 경유 칼럼을 숨긴다 */
  giftAgentView?: boolean;
}

const STATUS_FILTERS: { key: 'all' | GiftSettlementStatus; label: string }[] = [
  { key: 'all', label: '전체' },
  { key: GiftSettlementStatus.Draft, label: '정산집계' },
  { key: GiftSettlementStatus.Paid, label: '지급완료' },
];

const SupplyHistorySection: FC<HistorySectionProps> = ({
  isAdmin,
  refreshTrigger,
  giftAgentView = false,
}) => {
  const toast = useToast();
  // GiftAgent(마리)는 /admin이 아니라 /gift-agent 읽기 전용 prefix로 조회한다(백엔드가 마리 건만 스코프).
  const role = giftAgentView ? LowerRole.GiftAgent : LowerRole.Admin;
  const [{ loading, value: items }, fetchHistory] = useAsyncFn(
    () =>
      listGiftSupplySettlements(
        {},
        [
          { path: 'giftItemBrand', select: 'name settlementInfo' },
          // 상세보기 모달의 배송요청일/배송완료일/택배사/송장번호 표시용
          { path: 'lines.giftDelivery', populate: giftBrandSellerDeliveryPopulate },
        ],
        { sort: { year: -1, month: -1 } },
        role,
      ),
    [role],
  );

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory, refreshTrigger]);

  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | GiftSettlementStatus>('all');
  const [editTarget, setEditTarget] = useState<GiftSupplySettlement | null>(null);
  // 상세보기 모달에 띄울 정산 문서 (null이면 닫힘)
  const [detailTarget, setDetailTarget] = useState<GiftSupplySettlement | null>(null);
  const [paidModal, setPaidModal] = useState<{
    open: boolean;
    id: string;
    name: string;
    count: number;
    amount: number;
  }>({ open: false, id: '', name: '', count: 0, amount: 0 });

  const handlePatch = async (
    id: string,
    status: GiftSettlementStatus,
    label: string,
    executedAt?: string,
    override?: { totalAmount: number },
  ): Promise<boolean> => {
    setActionLoading(id);
    try {
      await patchGiftSupplySettlement(id, {
        status,
        ...(executedAt ? { executedAt } : {}),
        ...(override ? { overrideAmount: true, totalAmount: override.totalAmount } : {}),
      });
      toast.show(`${label} 완료`);
      fetchHistory();
      return true;
    } catch (e: any) {
      toast.show(`오류: ${e?.response?.data?.message || label + ' 실패'}`);
      return false;
    } finally {
      setActionLoading(null);
    }
  };

  const filtered = useMemo(() => {
    let all = (items ?? []) as GiftSupplySettlement[];
    // GiftAgent(마리)는 정산 주체가 마리인 업체정산만 본다
    if (giftAgentView) {
      all = all.filter(
        (s) =>
          typeof s.giftItemBrand === 'object' &&
          s.giftItemBrand.settlementInfo?.accountEntity === AccountEntity.Mari,
      );
    }
    return statusFilter === 'all' ? all : all.filter((s) => s.status === statusFilter);
  }, [items, statusFilter, giftAgentView]);
  const summary = useMemo(
    () => ({
      count: filtered.length,
      total: filtered.reduce((sum, s) => sum + settlementDisplayTotal(s), 0),
    }),
    [filtered],
  );
  const editBrandId =
    editTarget &&
    (typeof editTarget.giftItemBrand === 'object'
      ? editTarget.giftItemBrand._id
      : editTarget.giftItemBrand);

  if (loading)
    return (
      <div className="center-box py-8">
        <Loading />
      </div>
    );
  if (!items?.length)
    return (
      <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow">
        정산 내역이 없습니다.
      </div>
    );

  return (
    <div>
      {/* 상태 필터 + 요약 */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`rounded-full border px-3 py-1 text-sm ${
                statusFilter === f.key
                  ? 'border-indigo-600 bg-indigo-600 text-white'
                  : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <p className="text-sm text-gray-500">
            {summary.count}건 · 합계{' '}
            <span className="font-semibold text-rose-700">
              {formatWon(summary.total)}
            </span>
          </p>
          <button
            onClick={() => exportSupplyHistorySummaryExcel(filtered, giftAgentView)}
            disabled={filtered.length === 0}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <DownloadIcon className="h-4 w-4" />
            엑셀 다운로드
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg bg-white shadow">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500">
            <tr>
              <th className="px-3 py-2.5 text-left">업체</th>
              {!giftAgentView && <th className="px-3 py-2.5 text-left">정산 주체</th>}
              <th className="px-3 py-2.5 text-left">정산월</th>
              <th className="px-3 py-2.5 text-left">상태</th>
              <th className="px-3 py-2.5 text-right">건수</th>
              <th className="px-3 py-2.5 text-right">정산금액</th>
              <th className="px-3 py-2.5 text-left">지급일</th>
              <th className="px-3 py-2.5 text-left">메모</th>
              <th className="px-3 py-2.5 text-center">상세</th>
              {isAdmin && <th className="px-3 py-2.5 text-right">관리</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={(isAdmin ? 10 : 9) - (giftAgentView ? 1 : 0)}
                  className="px-3 py-10 text-center text-gray-400"
                >
                  해당 상태의 정산이 없습니다.
                </td>
              </tr>
            )}
            {filtered.map((s) => {
              const brand = s.giftItemBrand;
              const brandName = typeof brand === 'object' ? brand.name : brand || '-';
              const entity =
                typeof brand === 'object'
                  ? brand.settlementInfo?.accountEntity
                  : undefined;
              const isPaid = s.status === GiftSettlementStatus.Paid;
              const isActing = actionLoading === s._id;

              return (
                <tr key={s._id} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-3 py-3 font-medium text-gray-700">
                    {brandName}
                  </td>
                  {!giftAgentView && (
                    <td className="px-3 py-3">{accountEntityBadge(entity)}</td>
                  )}
                  <td className="whitespace-nowrap px-3 py-3 font-medium">
                    {s.year}년 {s.month}월
                  </td>
                  <td className="px-3 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusColor(
                        s.status,
                      )}`}
                    >
                      {statusLabel(s.status)}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-600">
                    {s.lines.length}건
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right font-semibold tabular-nums text-gray-800">
                    {formatWon(settlementDisplayTotal(s))}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                    {s.executedAt ? formatDate(s.executedAt) : '-'}
                  </td>
                  <td className="max-w-[140px] truncate px-3 py-3 text-xs text-gray-500">
                    {s.memo || '-'}
                  </td>
                  <td className="px-3 py-3 text-center">
                    <button
                      onClick={() => setDetailTarget(s)}
                      className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
                    >
                      상세보기
                    </button>
                  </td>
                  {isAdmin && (
                    <td className="whitespace-nowrap px-3 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => setEditTarget(s)}
                          className="rounded border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          수정
                        </button>
                        {!isPaid && (
                          <button
                            disabled={isActing}
                            onClick={() =>
                              setPaidModal({
                                open: true,
                                id: s._id,
                                name: brandName,
                                count: s.lines.length,
                                amount: s.totalAmount,
                              })
                            }
                            className="rounded bg-green-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
                          >
                            {isActing ? '처리 중...' : '지급확정'}
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {detailTarget && (
        <SupplySettlementDetailModal
          settlement={detailTarget}
          mySettlementInfo={
            typeof detailTarget.giftItemBrand === 'object'
              ? (detailTarget.giftItemBrand.settlementInfo as SettlementInfo | undefined)
              : undefined
          }
          onClose={() => setDetailTarget(null)}
        />
      )}

      <PaidConfirmModal
        open={paidModal.open}
        targetName={paidModal.name}
        count={paidModal.count}
        amount={paidModal.amount}
        onClose={() => setPaidModal((m) => ({ ...m, open: false }))}
        onConfirm={async (executedAt, paidAmount) => {
          // 입력한 실제 지급액을 그대로 동결(override)해 확정한다 (재계산·반올림 없음 — 화면 금액 = 지급액).
          const ok = await handlePatch(
            paidModal.id,
            GiftSettlementStatus.Paid,
            'Paid 확정',
            executedAt,
            { totalAmount: paidAmount },
          );
          if (ok) setPaidModal((m) => ({ ...m, open: false }));
        }}
      />
      {editTarget && editBrandId && (
        <SettlementEditModal
          open
          purpose="supply"
          settlement={editTarget}
          targetName={
            typeof editTarget.giftItemBrand === 'object'
              ? editTarget.giftItemBrand.name
              : '업체정산'
          }
          fetchEligible={() =>
            getSettlementPending(
              'supply',
              { giftItemBrand: editBrandId },
              LowerRole.Admin,
            )
          }
          patch={patchGiftSupplySettlement}
          remove={deleteGiftSupplySettlement}
          onClose={() => setEditTarget(null)}
          onSaved={fetchHistory}
        />
      )}
      <Toast message={toast.message} durationMs={toast.durationMs} />
    </div>
  );
};

// ───────────────────────── 페이지 ─────────────────────────

export const GiftSettlementSupplyPage: FC = function GiftSettlementSupplyPage() {
  const { isAdmin, isGiftAgent } = useAuth();
  const canWrite = isAdmin() && !isGiftAgent();
  // GiftAgent(마리) 조회 뷰: 마리 정산건만 노출 + 경유 칩/칼럼 숨김
  const giftAgentView = !isAdmin() && isGiftAgent();

  // 집행 후 내역 섹션 강제 리프레시
  const [historyRefresh, setHistoryRefresh] = useState(0);
  const handleSettled = useCallback(() => {
    setHistoryRefresh((n) => n + 1);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">배송업체 정산 관리</h1>
          <p className="mt-1 text-sm text-gray-500">
            공급 업체 배송형 정산 조회{canWrite ? ' · 집행 · 수정 · 확정' : ''}
          </p>
        </div>

        {/* 미정산 내역 (상단) */}
        <section className="mb-10">
          <h2 className="mb-4 text-lg font-bold text-gray-800">미정산 내역</h2>
          <SupplyPendingSection
            isAdmin={canWrite}
            onSettled={handleSettled}
            giftAgentView={giftAgentView}
          />
        </section>

        {/* 정산 완료 내역 (하단) */}
        <section>
          <h2 className="mb-4 text-lg font-bold text-gray-800">정산 완료 내역</h2>
          <SupplyHistorySection
            isAdmin={canWrite}
            refreshTrigger={historyRefresh}
            giftAgentView={giftAgentView}
          />
        </section>
      </div>
    </div>
  );
};
